import React, { useState, useEffect, useCallback } from "react";
import {
  Row,
  Col,
  Card,
  Button,
  Form,
  Input,
  InputNumber,
  Switch,
  Table,
  Typography,
  Space,
  Tag,
  Tooltip,
  Popconfirm,
  Modal,
  message,
  Spin,
  Empty,
  Badge,
  Divider,
} from "antd";
import {
  PlusOutlined,
  EditOutlined,
  DeleteOutlined,
  ReloadOutlined,
  KeyOutlined,
  UnorderedListOutlined,
} from "@ant-design/icons";
import dayjs from "dayjs";
import axios from "axios";
// 业务组件顶部导入
import {
  getDictItemsByType,
  deleteDictItem,
  createDictItem,
  updateDictItem,
  getDictTypes,
  deleteDictType,
  createDictType,
  updateDictType,
} from "../api/dict"; // 按你的实际路径调整
import { clearDictCache } from "../DictCache";

const { Title, Text, Paragraph } = Typography;

// ─────────────────────────────────────────────
// DictType 表单弹窗
// ─────────────────────────────────────────────
function DictTypeModal({ open, mode, initialValues, onOk, onCancel }) {
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (open) {
      form.resetFields();
      if (mode === "edit" && initialValues) {
        form.setFieldsValue({
          label: initialValues.label,
          description: initialValues.description,
          ttl: initialValues.ttl,
        });
      }
    }
  }, [open, mode, initialValues, form]);

  const handleOk = async () => {
    try {
      const values = await form.validateFields();
      setLoading(true);
      await onOk(values);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Modal
      title={mode === "edit" ? "编辑字典类型" : "新增字典类型"}
      open={open}
      onOk={handleOk}
      onCancel={onCancel}
      confirmLoading={loading}
      destroyOnClose
      width={480}
    >
      <Form form={form} layout="vertical" style={{ marginTop: 16 }}>
        {mode === "add" && (
          <Form.Item
            name="type"
            label="类型 Key"
            rules={[
              { required: true, message: "请输入类型 Key" },
              {
                pattern: /^[a-z][a-z0-9_]*$/,
                message: "只允许小写字母、数字和下划线，且以字母开头",
              },
            ]}
            extra="唯一标识，创建后不可修改，如：asset_status"
          >
            <Input placeholder="asset_status" maxLength={50} />
          </Form.Item>
        )}
        <Form.Item
          name="label"
          label="显示名称"
          rules={[{ required: true, message: "请输入显示名称" }]}
        >
          <Input placeholder="如：资产状态" maxLength={50} />
        </Form.Item>
        <Form.Item name="description" label="备注说明">
          <Input.TextArea
            placeholder="说明该枚举类型的用途"
            rows={2}
            maxLength={200}
            showCount
          />
        </Form.Item>
        <Form.Item
          name="ttl"
          label="前端缓存时长（分钟）"
          initialValue={60}
          extra="前端加载后缓存的有效时间，变动频繁的类型可设置较短时间"
        >
          <InputNumber min={1} max={10080} style={{ width: "100%" }} />
        </Form.Item>
      </Form>
    </Modal>
  );
}

// ─────────────────────────────────────────────
// DictItem 表单弹窗
// ─────────────────────────────────────────────
function DictItemModal({
  open,
  mode,
  initialValues,
  dictType,
  onOk,
  onCancel,
}) {
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (open) {
      form.resetFields();
      if (mode === "edit" && initialValues) {
        form.setFieldsValue({
          label: initialValues.label,
          value: initialValues.value,
          sort: initialValues.sort,
          status: initialValues.status === 1,
        });
      } else {
        form.setFieldsValue({ status: true, sort: 0 });
      }
    }
  }, [open, mode, initialValues, form]);

  const handleOk = async () => {
    try {
      const values = await form.validateFields();
      setLoading(true);
      await onOk({
        ...values,
        status: values.status ? 1 : 0,
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <Modal
      title={
        mode === "edit"
          ? "编辑枚举值"
          : `新增枚举值（${dictType?.label || ""}）`
      }
      open={open}
      onOk={handleOk}
      onCancel={onCancel}
      confirmLoading={loading}
      destroyOnClose
      width={480}
    >
      <Form form={form} layout="vertical" style={{ marginTop: 16 }}>
        <Form.Item
          name="label"
          label="显示文本"
          rules={[{ required: true, message: "请输入显示文本" }]}
        >
          <Input placeholder="如：在用" maxLength={50} />
        </Form.Item>
        <Form.Item
          name="value"
          label="存储值"
          rules={[
            { required: true, message: "请输入存储值" },
            {
              pattern: /^[a-zA-Z][a-zA-Z0-9_]*$/,
              message: "建议使用字母、数字和下划线，以字母开头",
            },
          ]}
          extra={
            mode === "edit"
              ? "修改存储值可能影响已有业务数据，请谨慎操作"
              : "实际存入数据库的值，如：active"
          }
        >
          <Input placeholder="active" maxLength={50} />
        </Form.Item>
        <Row gutter={16}>
          <Col span={12}>
            <Form.Item name="sort" label="排序">
              <InputNumber
                min={0}
                style={{ width: "100%" }}
                placeholder="数字越小越靠前"
              />
            </Form.Item>
          </Col>
          <Col span={12}>
            <Form.Item name="status" label="状态" valuePropName="checked">
              <Switch checkedChildren="启用" unCheckedChildren="禁用" />
            </Form.Item>
          </Col>
        </Row>
      </Form>
    </Modal>
  );
}

// ─────────────────────────────────────────────
// 右侧：枚举值列表
// ─────────────────────────────────────────────
function DictItemPanel({ dictType, onItemChange }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  const [modalMode, setModalMode] = useState("add");
  const [editingItem, setEditingItem] = useState(null);

  const fetchItems = useCallback(async () => {
    if (!dictType) return;
    setLoading(true);
    try {
      const res = await getDictItemsByType(dictType.type);
      if (res.data.code === 0) setItems(res.data.data.items);
    } finally {
      setLoading(false);
    }
    // try {
    //   const res = await axios.get(`/api/dict/items/${dictType.type}`);
    //   if (res.data.code === 0) setItems(res.data.data.items);
    // } finally {
    //   setLoading(false);
    // }
  }, [dictType]);

  useEffect(() => {
    setItems([]);
    fetchItems();
  }, [fetchItems]);

  const handleAdd = () => {
    setEditingItem(null);
    setModalMode("add");
    setModalOpen(true);
  };

  const handleEdit = (item) => {
    setEditingItem(item);
    setModalMode("edit");
    setModalOpen(true);
  };

  const handleDelete = async (item) => {
    try {
      // const res = await axios.delete(`/api/dict/items/${item._id}`);
      const res = await deleteDictItem(item._id);
      if (res.data.code === 0) {
        message.success("删除成功");
        clearDictCache(dictType.type);
        onItemChange && onItemChange(dictType.type);
        fetchItems();
      } else {
        message.error(res.data.message);
      }
    } catch (err) {
      message.error(err.response?.data?.message || "删除失败");
    }
  };

  const handleModalOk = async (values) => {
    try {
      let res;
      if (modalMode === "edit") {
        res = await updateDictItem(editingItem._id, values);
      } else {
        res = await createDictItem({
          ...values,
          type: dictType.type,
        });
      }
      // if (modalMode === "edit") {
      //   res = await axios.put(`/api/dict/items/${editingItem._id}`, values);
      // } else {
      //   res = await axios.post("/api/dict/items", {
      //     ...values,
      //     type: dictType.type,
      //   });
      // }
      if (res.data.code === 0) {
        message.success(modalMode === "edit" ? "编辑成功" : "新增成功");
        setModalOpen(false);
        clearDictCache(dictType.type);
        onItemChange && onItemChange(dictType.type);
        fetchItems();
      } else {
        message.error(res.data.message);
      }
    } catch (err) {
      message.error(err.response?.data?.message || "操作失败");
    }
  };

  const columns = [
    {
      title: "排序",
      dataIndex: "sort",
      key: "sort",
      width: 60,
      align: "center",
      render: (v) => (
        <Text type="secondary" style={{ fontSize: 12 }}>
          {v ?? 0}
        </Text>
      ),
    },
    {
      title: "显示文本",
      dataIndex: "label",
      key: "label",
      width: 120,
      render: (v) => <Text strong>{v}</Text>,
    },
    {
      title: "存储值",
      dataIndex: "value",
      key: "value",
      width: 140,
      render: (v) => (
        <Tag
          icon={<KeyOutlined />}
          color="blue"
          style={{ fontFamily: "monospace" }}
        >
          {v}
        </Tag>
      ),
    },
    {
      title: "状态",
      dataIndex: "status",
      key: "status",
      width: 80,
      render: (v) =>
        v === 1 ? (
          <Badge status="success" text="启用" />
        ) : (
          <Badge status="default" text="禁用" />
        ),
    },
    {
      title: "创建时间",
      dataIndex: "createdAt",
      key: "createdAt",
      width: 120,
      render: (v) => (
        <Text type="secondary" style={{ fontSize: 12 }}>
          {v ? dayjs(v).format("YYYY-MM-DD") : "-"}
        </Text>
      ),
    },
    {
      title: "操作",
      key: "action",
      width: 90,
      render: (_, record) => (
        <Space size={4}>
          <Tooltip title="编辑">
            <Button
              type="text"
              size="small"
              icon={<EditOutlined />}
              onClick={() => handleEdit(record)}
            />
          </Tooltip>
          <Popconfirm
            title="确认删除"
            description="请确认该枚举值未被业务数据引用"
            onConfirm={() => handleDelete(record)}
            okText="删除"
            okButtonProps={{ danger: true }}
            cancelText="取消"
          >
            <Tooltip title="删除">
              <Button
                type="text"
                size="small"
                danger
                icon={<DeleteOutlined />}
              />
            </Tooltip>
          </Popconfirm>
        </Space>
      ),
    },
  ];

  if (!dictType) {
    return (
      <div
        style={{
          height: "100%",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        <Empty description="请在左侧选择一个字典类型" />
      </div>
    );
  }

  return (
    <div style={{ height: "100%", display: "flex", flexDirection: "column" }}>
      {/* 类型信息头 */}
      <div style={{ marginBottom: 16 }}>
        <Space
          align="start"
          style={{ justifyContent: "space-between", width: "100%" }}
        >
          <div>
            <Space align="center">
              <Title level={5} style={{ margin: 0 }}>
                {dictType.label}
              </Title>
              <Tag
                icon={<KeyOutlined />}
                color="blue"
                style={{ fontFamily: "monospace" }}
              >
                {dictType.type}
              </Tag>
              <Tag color="default">TTL {dictType.ttl} 分钟</Tag>
            </Space>
            {dictType.description && (
              <Paragraph
                type="secondary"
                style={{ margin: "4px 0 0", fontSize: 12 }}
              >
                {dictType.description}
              </Paragraph>
            )}
          </div>
          <Space>
            <Button
              size="small"
              icon={<ReloadOutlined />}
              onClick={fetchItems}
            />
            <Button
              type="primary"
              size="small"
              icon={<PlusOutlined />}
              onClick={handleAdd}
            >
              新增枚举值
            </Button>
          </Space>
        </Space>
      </div>

      <Divider style={{ margin: "0 0 12px" }} />

      {/* 枚举值表格 */}
      <div style={{ flex: 1, overflow: "auto" }}>
        <Table
          rowKey="_id"
          dataSource={items}
          columns={columns}
          loading={loading}
          size="small"
          pagination={false}
          locale={{
            emptyText: (
              <Empty
                description="暂无枚举值"
                image={Empty.PRESENTED_IMAGE_SIMPLE}
              />
            ),
          }}
        />
      </div>

      {/* 弹窗 */}
      <DictItemModal
        open={modalOpen}
        mode={modalMode}
        initialValues={editingItem}
        dictType={dictType}
        onOk={handleModalOk}
        onCancel={() => setModalOpen(false)}
      />
    </div>
  );
}

// ─────────────────────────────────────────────
// 主页面
// ─────────────────────────────────────────────
export default function DictManagePage() {
  const [dictTypes, setDictTypes] = useState([]);
  const [typesLoading, setTypesLoading] = useState(false);
  const [selectedType, setSelectedType] = useState(null);

  // 类型弹窗
  const [typeModalOpen, setTypeModalOpen] = useState(false);
  const [typeModalMode, setTypeModalMode] = useState("add");
  const [editingType, setEditingType] = useState(null);

  // ── 拉取类型列表 ──
  const fetchTypes = useCallback(async () => {
    setTypesLoading(true);
    try {
      // const res = await axios.get("/api/dict/types");
      const res = await getDictTypes();
      if (res.data.code === 0) {
        setDictTypes(res.data.data);
        // 保持选中状态
        if (selectedType) {
          const updated = res.data.data.find((t) => t._id === selectedType._id);
          setSelectedType(updated || null);
        }
      }
    } finally {
      setTypesLoading(false);
    }
  }, [selectedType]);

  useEffect(() => {
    fetchTypes();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ── 新增类型 ──
  const handleAddType = () => {
    setEditingType(null);
    setTypeModalMode("add");
    setTypeModalOpen(true);
  };

  // ── 编辑类型 ──
  const handleEditType = (e, dictType) => {
    e.stopPropagation();
    setEditingType(dictType);
    setTypeModalMode("edit");
    setTypeModalOpen(true);
  };

  // ── 删除类型 ──
  const handleDeleteType = async (e, dictType) => {
    e.stopPropagation();
    try {
      // const res = await axios.delete(`/api/dict/types/${dictType._id}`);
      const res = await deleteDictType(dictType._id);
      if (res.data.code === 0) {
        message.success("删除成功");
        if (selectedType?._id === dictType._id) setSelectedType(null);
        fetchTypes();
      } else {
        message.error(res.data.message);
      }
    } catch (err) {
      message.error(err.response?.data?.message || "删除失败");
    }
  };

  // ── 类型弹窗提交 ──
  const handleTypeModalOk = async (values) => {
    try {
      // let res;
      let res;
      if (typeModalMode === "edit") {
        res = await updateDictType(editingType._id, values);
      } else {
        res = await createDictType(values);
      }
      // if (typeModalMode === "edit") {
      //   res = await axios.put(`/api/dict/types/${editingType._id}`, values);
      // } else {
      //   res = await axios.post("/api/dict/types", values);
      // }
      if (res.data.code === 0) {
        message.success(typeModalMode === "edit" ? "编辑成功" : "新增成功");
        setTypeModalOpen(false);
        fetchTypes();
      } else {
        message.error(res.data.message);
      }
    } catch (err) {
      message.error(err.response?.data?.message || "操作失败");
    }
  };

  // 枚举值变更后刷新缓存（通知 Context）
  const handleItemChange = (type) => {
    clearDictCache(type);
  };

  return (
    <div
      style={{
        padding: "16px 24px",
        height: "100%",
        display: "flex",
        flexDirection: "column",
      }}
    >
      {/* 页面标题 */}
      <div style={{ marginBottom: 16 }}>
        <Title level={4} style={{ margin: 0 }}>
          数据字典管理
        </Title>
        <Text type="secondary">统一管理系统中的枚举值，支持前端双层缓存</Text>
      </div>

      <Row gutter={16} style={{ flex: 1, minHeight: 0, overflow: "hidden" }}>
        {/* ── 左侧：字典类型列表 ── */}
        <Col
          span={7}
          style={{ height: "100%", display: "flex", flexDirection: "column" }}
        >
          <Card
            size="small"
            title={
              <Space>
                <UnorderedListOutlined />
                字典类型
              </Space>
            }
            extra={
              <Button
                type="primary"
                size="small"
                icon={<PlusOutlined />}
                onClick={handleAddType}
              >
                新增
              </Button>
            }
            style={{
              flex: 1,
              overflow: "hidden",
              display: "flex",
              flexDirection: "column",
            }}
            bodyStyle={{ flex: 1, overflowY: "auto", padding: "4px 0" }}
          >
            {typesLoading ? (
              <div style={{ textAlign: "center", paddingTop: 40 }}>
                <Spin />
              </div>
            ) : dictTypes.length === 0 ? (
              <Empty
                description="暂无字典类型"
                image={Empty.PRESENTED_IMAGE_SIMPLE}
                style={{ marginTop: 40 }}
              >
                <Button
                  type="primary"
                  icon={<PlusOutlined />}
                  onClick={handleAddType}
                >
                  新增类型
                </Button>
              </Empty>
            ) : (
              <div>
                {dictTypes.map((dt) => {
                  const isSelected = selectedType?._id === dt._id;
                  return (
                    <div
                      key={dt._id}
                      onClick={() => setSelectedType(dt)}
                      style={{
                        padding: "8px 16px",
                        cursor: "pointer",
                        backgroundColor: isSelected ? "#e6f4ff" : "transparent",
                        borderRight: isSelected
                          ? "2px solid #1677ff"
                          : "2px solid transparent",
                        transition: "all 0.15s",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "space-between",
                      }}
                    >
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <Text
                          strong={isSelected}
                          ellipsis
                          style={{ display: "block", fontSize: 13 }}
                        >
                          {dt.label}
                        </Text>
                        <Text
                          type="secondary"
                          style={{
                            fontSize: 11,
                            fontFamily: "monospace",
                            display: "block",
                          }}
                        >
                          {dt.type}
                        </Text>
                      </div>
                      {/* 操作按钮 */}
                      <Space size={2} onClick={(e) => e.stopPropagation()}>
                        <Tooltip title="编辑">
                          <Button
                            type="text"
                            size="small"
                            icon={<EditOutlined style={{ fontSize: 11 }} />}
                            onClick={(e) => handleEditType(e, dt)}
                            style={{ padding: "0 4px", height: 20 }}
                          />
                        </Tooltip>
                        <Popconfirm
                          title="确认删除"
                          description="需先删除该类型下所有枚举值"
                          onConfirm={(e) => handleDeleteType(e, dt)}
                          okText="删除"
                          okButtonProps={{ danger: true }}
                          cancelText="取消"
                          onClick={(e) => e.stopPropagation()}
                        >
                          <Tooltip title="删除">
                            <Button
                              type="text"
                              size="small"
                              danger
                              icon={<DeleteOutlined style={{ fontSize: 11 }} />}
                              style={{ padding: "0 4px", height: 20 }}
                            />
                          </Tooltip>
                        </Popconfirm>
                      </Space>
                    </div>
                  );
                })}
              </div>
            )}
          </Card>
        </Col>

        {/* ── 右侧：枚举值管理 ── */}
        <Col span={17} style={{ height: "100%", overflow: "hidden" }}>
          <Card
            size="small"
            style={{ height: "100%" }}
            bodyStyle={{ height: "100%", overflow: "hidden", padding: "16px" }}
          >
            <DictItemPanel
              dictType={selectedType}
              onItemChange={handleItemChange}
            />
          </Card>
        </Col>
      </Row>

      {/* 字典类型弹窗 */}
      <DictTypeModal
        open={typeModalOpen}
        mode={typeModalMode}
        initialValues={editingType}
        onOk={handleTypeModalOk}
        onCancel={() => setTypeModalOpen(false)}
      />
    </div>
  );
}
